import React, { useState } from 'react';
import {
  Home, DollarSign, BarChart3, FolderKanban, Settings,
  Bell, Search, ChevronsLeft, ChevronsRight, Sun, Moon,
  Plus, TrendingUp, Clock, AlertTriangle, CheckCircle2,
  FileText, MessageSquare, Activity, Download, Eye,
  ArrowLeft, MoreHorizontal, Calendar, Building2, User
} from 'lucide-react';

/**
 * PÁGINA DE PREVIEW VISUAL — no afecta las páginas reales.
 * Muestra el rediseño propuesto de:
 *  - Sidebar lateral colapsable
 *  - Dashboard con KPIs, actividad reciente y lista
 *  - PaymentDetail 2 columnas con timeline
 *  - Badges expresivos
 * Todos los datos son estáticos (mock).
 */

const LOGO_MARK = 'https://customer-assets.emergentagent.com/job_b0e0e0df-2d06-4315-be1e-7d0387fd3a85/artifacts/eslg6eoj_imagen%282%29.png';

export default function DesignPreview() {
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'detail'
  const [collapsed, setCollapsed] = useState(false);
  const [dark, setDark] = useState(false);
  const [listMode, setListMode] = useState('cards'); // 'cards' | 'table'

  const bgApp = dark ? 'bg-slate-950' : 'bg-slate-50';
  const bgSurface = dark ? 'bg-slate-900' : 'bg-white';
  const border = dark ? 'border-slate-800' : 'border-slate-200';
  const textPrimary = dark ? 'text-slate-100' : 'text-slate-900';
  const textSec = dark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className={`min-h-screen ${bgApp} font-sans transition-colors duration-200`} style={{fontFamily: '"IBM Plex Sans", "Manrope", system-ui, sans-serif'}}>
      <div className="flex h-screen">
        {/* SIDEBAR */}
        <aside className={`${collapsed ? 'w-16' : 'w-60'} ${bgSurface} border-r ${border} flex flex-col transition-[width] duration-300`}>
          {/* Logo */}
          <div className={`h-16 flex items-center ${collapsed ? 'justify-center' : 'px-5 gap-3'} border-b ${border}`}>
            <img src={LOGO_MARK} alt="SIEMPRE" className="h-9 w-9 object-contain" />
            {!collapsed && (
              <div>
                <p className={`font-bold text-sm ${textPrimary} leading-tight`} style={{fontFamily: 'Manrope, sans-serif'}}>SIEMPRE</p>
                <p className={`text-[10px] ${textSec} uppercase tracking-widest`}>Portal Pagos</p>
              </div>
            )}
          </div>

          {/* Nav items */}
          <nav className="flex-1 py-4 space-y-1">
            <NavItem icon={Home} label="Inicio" active collapsed={collapsed} dark={dark} />
            <NavItem icon={DollarSign} label="Pagos" collapsed={collapsed} dark={dark} badge="8" />
            <NavItem icon={BarChart3} label="Reportes" collapsed={collapsed} dark={dark} />
            <NavItem icon={FolderKanban} label="Catálogos" collapsed={collapsed} dark={dark} />
            <NavItem icon={Settings} label="Administración" collapsed={collapsed} dark={dark} />
          </nav>

          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`mx-3 mb-4 h-9 flex items-center justify-center rounded-md ${dark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'} transition-colors`}
          >
            {collapsed ? <ChevronsRight className="h-4 w-4"/> : <ChevronsLeft className="h-4 w-4"/>}
          </button>
        </aside>

        {/* MAIN COLUMN */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* HEADER */}
          <header className={`h-16 ${bgSurface} border-b ${border} flex items-center justify-between px-6 sticky top-0 z-10`}>
            <div className="flex items-center gap-4">
              <button onClick={() => setView('dashboard')} className={`text-xs uppercase tracking-widest font-semibold ${view==='dashboard' ? 'text-[#2c57c5]' : textSec} hover:text-[#2c57c5]`}>Dashboard</button>
              <span className={textSec}>·</span>
              <button onClick={() => setView('detail')} className={`text-xs uppercase tracking-widest font-semibold ${view==='detail' ? 'text-[#2c57c5]' : textSec} hover:text-[#2c57c5]`}>Detalle de Pago</button>
            </div>
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className={`hidden lg:flex items-center gap-2 px-3 h-9 rounded-md border ${border} ${dark ? 'bg-slate-900' : 'bg-slate-50'}`}>
                <Search className={`h-4 w-4 ${textSec}`}/>
                <input placeholder="Buscar..." className={`bg-transparent outline-none text-sm w-48 ${textPrimary} placeholder:${textSec}`}/>
                <kbd className={`text-[10px] ${textSec} border ${border} rounded px-1 py-0.5`}>⌘K</kbd>
              </div>
              {/* Dark toggle */}
              <button onClick={() => setDark(!dark)} className={`h-9 w-9 flex items-center justify-center rounded-md hover:${dark ? 'bg-slate-800' : 'bg-slate-100'} ${textSec} transition-colors`}>
                {dark ? <Sun className="h-4 w-4"/> : <Moon className="h-4 w-4"/>}
              </button>
              {/* Notifications */}
              <button className={`relative h-9 w-9 flex items-center justify-center rounded-md hover:${dark ? 'bg-slate-800' : 'bg-slate-100'} ${textSec}`}>
                <Bell className="h-4 w-4"/>
                <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-500"/>
              </button>
              {/* User */}
              <div className={`flex items-center gap-2 pl-3 border-l ${border}`}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#2c57c5] to-[#1e3a8a] flex items-center justify-center text-white text-xs font-semibold">AN</div>
                <div className="text-left">
                  <p className={`text-xs font-semibold ${textPrimary}`}>Andrea Núñez</p>
                  <p className={`text-[10px] ${textSec}`}>Gerencia</p>
                </div>
              </div>
            </div>
          </header>

          {/* CONTENT */}
          <main className="flex-1 overflow-y-auto p-8">
            {view === 'dashboard' && <DashboardView dark={dark} listMode={listMode} setListMode={setListMode} />}
            {view === 'detail' && <DetailView dark={dark} />}
          </main>
        </div>
      </div>
    </div>
  );
}

function NavItem({ icon: Icon, label, active, collapsed, dark, badge }) {
  const activeStyle = active
    ? `${dark ? 'bg-blue-950/50' : 'bg-blue-50'} text-[#2c57c5] border-l-[3px] border-[#2c57c5]`
    : `${dark ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-100' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'} border-l-[3px] border-transparent`;
  return (
    <button className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3 pl-4 pr-3'} h-10 transition-all ${activeStyle}`}>
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && (
        <>
          <span className="text-sm font-medium">{label}</span>
          {badge && <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded ${active ? 'bg-[#2c57c5] text-white' : 'bg-red-500 text-white'}`}>{badge}</span>}
        </>
      )}
    </button>
  );
}

function DashboardView({ dark, listMode, setListMode }) {
  const bgSurface = dark ? 'bg-slate-900' : 'bg-white';
  const border = dark ? 'border-slate-800' : 'border-slate-200';
  const textPrimary = dark ? 'text-slate-100' : 'text-slate-900';
  const textSec = dark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between mb-8 animate-fadeup" style={{animationDelay: '0ms'}}>
        <div>
          <h1 className={`text-3xl font-bold ${textPrimary}`} style={{fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em'}}>Inicio</h1>
          <p className={`text-sm ${textSec} mt-1`}>Hola, Andrea 👋 · Miércoles 24 de febrero</p>
        </div>
        <button className="h-10 px-5 bg-[#2c57c5] hover:bg-[#22459c] text-white text-sm font-medium rounded-md flex items-center gap-2 shadow-sm active:scale-95 transition-all">
          <Plus className="h-4 w-4"/> Nuevo Pago
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <KPI dark={dark} icon={FileText} label="Total de Pagos" value="42" trend="+8" tone="blue" delay={50}/>
        <KPI dark={dark} icon={Clock} label="Pendientes" value="8" trend="↑ 2 ayer" tone="amber" pulse delay={100}/>
        <KPI dark={dark} icon={DollarSign} label="Monto Hoy" value="Q125,300" trend="+12%" tone="indigo" delay={150}/>
        <KPI dark={dark} icon={AlertTriangle} label="Vencidos" value="3" trend="Contado" tone="red" delay={200}/>
      </div>

      {/* Body: list + activity */}
      <div className="grid grid-cols-4 gap-4">
        {/* Payments list */}
        <div className={`col-span-3 ${bgSurface} border ${border} rounded-lg overflow-hidden animate-fadeup`} style={{animationDelay: '250ms'}}>
          <div className={`p-5 border-b ${border} flex items-center justify-between`}>
            <div>
              <h2 className={`text-lg font-semibold ${textPrimary}`} style={{fontFamily: 'Manrope, sans-serif'}}>Todos los Pagos</h2>
              <p className={`text-xs ${textSec} mt-0.5`}>42 pagos · filtrado: Hoy · Pendiente</p>
            </div>
            <div className={`flex items-center border ${border} rounded-md overflow-hidden text-xs`}>
              <button onClick={() => setListMode('cards')} className={`px-3 h-8 ${listMode==='cards' ? 'bg-[#2c57c5] text-white' : textSec}`}>Tarjetas</button>
              <button onClick={() => setListMode('table')} className={`px-3 h-8 ${listMode==='table' ? 'bg-[#2c57c5] text-white' : textSec}`}>Tabla</button>
            </div>
          </div>

          {/* Filters bar */}
          <div className={`px-5 py-3 border-b ${border} flex items-center gap-2 flex-wrap ${dark ? 'bg-slate-950/50' : 'bg-slate-50/50'}`}>
            <FilterPill dark={dark} label="Hoy" active/>
            <FilterPill dark={dark} label="Pendiente" active/>
            <FilterPill dark={dark} label="Todos los proveedores"/>
            <div className="ml-auto">
              <div className={`flex items-center gap-2 px-3 h-8 rounded-md border ${border} ${dark ? 'bg-slate-900' : 'bg-white'}`}>
                <Search className={`h-3.5 w-3.5 ${textSec}`}/>
                <input placeholder="Buscar proveedor..." className={`bg-transparent outline-none text-xs w-48 ${textPrimary}`}/>
              </div>
            </div>
          </div>

          {/* List content */}
          {listMode === 'cards' ? (
            <div className="p-4 space-y-2">
              <PaymentRow dark={dark} provider="BIMBO S.A." store="Zona 10" amount="Q3,500.00" status="pagado" time="Hace 10 min"/>
              <PaymentRow dark={dark} provider="COCA COLA GT" store="Zona 4" amount="Q1,200.00" status="pendiente" time="Hace 25 min"/>
              <PaymentRow dark={dark} provider="Café del Carmen" store="Antigua" amount="Q890.00" status="vencido" time="Ayer 4:30 PM" alert/>
              <PaymentRow dark={dark} provider="Distribuidora Norte" store="Xela" amount="Q5,678.00" status="aprobado" time="Hoy 09:15"/>
              <PaymentRow dark={dark} provider="Lácteos del Valle" store="Zona 15" amount="Q2,340.00" status="pendiente" time="Hoy 10:22"/>
            </div>
          ) : (
            <div className="overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`text-[10px] uppercase tracking-wider ${textSec} border-b ${border}`}>
                    <th className="text-left px-5 py-3 font-semibold">Proveedor</th>
                    <th className="text-left px-5 py-3 font-semibold">Tienda</th>
                    <th className="text-right px-5 py-3 font-semibold">Monto</th>
                    <th className="text-left px-5 py-3 font-semibold">Estado</th>
                    <th className="text-left px-5 py-3 font-semibold">Fecha</th>
                    <th className="text-right px-5 py-3 font-semibold">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['BIMBO S.A.','Zona 10','Q3,500.00','pagado','Hace 10 min'],
                    ['COCA COLA GT','Zona 4','Q1,200.00','pendiente','Hace 25 min'],
                    ['Café del Carmen','Antigua','Q890.00','vencido','Ayer 4:30 PM'],
                    ['Distribuidora Norte','Xela','Q5,678.00','aprobado','Hoy 09:15'],
                    ['Lácteos del Valle','Zona 15','Q2,340.00','pendiente','Hoy 10:22'],
                  ].map(([p,s,a,st,t], i) => (
                    <tr key={i} className={`border-b ${border} hover:${dark?'bg-slate-800/50':'bg-slate-50'} transition-colors`}>
                      <td className={`px-5 py-3 font-medium ${textPrimary}`}>{p}</td>
                      <td className={`px-5 py-3 ${textSec}`}>{s}</td>
                      <td className={`px-5 py-3 text-right font-mono ${textPrimary}`} style={{fontVariantNumeric:'tabular-nums'}}>{a}</td>
                      <td className="px-5 py-3"><StatusBadge status={st}/></td>
                      <td className={`px-5 py-3 ${textSec}`}>{t}</td>
                      <td className="px-5 py-3 text-right">
                        <button className={`text-${textSec} hover:text-[#2c57c5]`}><MoreHorizontal className="h-4 w-4 inline"/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Activity widget */}
        <div className={`col-span-1 ${bgSurface} border ${border} rounded-lg p-5 animate-fadeup`} style={{animationDelay: '300ms'}}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`font-semibold ${textPrimary}`} style={{fontFamily: 'Manrope, sans-serif'}}>Actividad Reciente</h3>
            <span className={`text-[10px] ${textSec}`}>Últimas 24h</span>
          </div>
          <div className="space-y-4">
            <ActivityItem dark={dark} initials="AN" name="Andrea" action="aprobó" refCode="Pago #A5F3" amount="Q3,500" time="hace 2 min" tone="green"/>
            <ActivityItem dark={dark} initials="CR" name="Carlos" action="registró" refCode="Pago #B7A2" amount="Q890" time="hace 15 min" tone="blue"/>
            <ActivityItem dark={dark} initials="LM" name="Luis" action="rechazó" refCode="Pago #C1F8" amount="Q2,100" time="hace 1h" tone="red"/>
            <ActivityItem dark={dark} initials="MB" name="María" action="marcó ERP" refCode="Pago #D9E4" amount="Q5,678" time="hace 2h" tone="slate"/>
            <ActivityItem dark={dark} initials="JP" name="Juan" action="subió" refCode="Pago #E2A1" amount="Q1,200" time="hace 3h" tone="blue"/>
          </div>
          <button className={`mt-4 text-xs font-semibold text-[#2c57c5] hover:underline`}>Ver toda la actividad →</button>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, trend, tone, dark, pulse, delay }) {
  const tones = {
    blue: 'text-[#2c57c5] bg-blue-50 border-blue-100',
    amber: 'text-amber-700 bg-amber-50 border-amber-100',
    indigo: 'text-indigo-700 bg-indigo-50 border-indigo-100',
    red: 'text-red-700 bg-red-50 border-red-100',
  };
  const trendColors = { blue: 'text-slate-500', amber: 'text-amber-600', indigo: 'text-green-600', red: 'text-red-600' };
  const surface = dark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200';
  const label_c = dark ? 'text-slate-500' : 'text-slate-500';
  const val_c = dark ? 'text-slate-100' : 'text-slate-900';
  return (
    <div className={`${surface} border rounded-lg p-5 hover:-translate-y-[1px] hover:shadow-sm transition-all animate-fadeup`} style={{animationDelay: `${delay}ms`}}>
      <div className="flex items-start justify-between mb-3">
        <p className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${label_c}`}>{label}</p>
        <div className={`h-8 w-8 rounded-md ${tones[tone].split(' ')[1]} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${tones[tone].split(' ')[0]}`}/>
        </div>
      </div>
      <p className={`text-3xl font-bold ${val_c}`} style={{fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums'}}>{value}</p>
      <div className="flex items-center gap-1 mt-2">
        {pulse && <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping"/><span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"/></span>}
        <p className={`text-xs font-medium ${trendColors[tone]}`}>{trend}</p>
      </div>
    </div>
  );
}

function FilterPill({ label, active, dark }) {
  const activeC = 'bg-[#2c57c5] text-white border-[#2c57c5]';
  const inactiveC = dark ? 'text-slate-400 border-slate-800 hover:bg-slate-800' : 'text-slate-600 border-slate-200 hover:bg-slate-50';
  return (
    <span className={`text-xs font-medium px-3 h-7 flex items-center rounded-full border transition-colors cursor-pointer ${active ? activeC : inactiveC}`}>
      {label}
      {active && <span className="ml-2 text-white/70">✕</span>}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    pagado:    { c: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: <CheckCircle2 className="h-3 w-3"/>, label: 'Pagado' },
    pendiente: { c: 'bg-amber-50 text-amber-700 border-amber-200', icon: <Clock className="h-3 w-3"/>, label: 'Pendiente' },
    aprobado:  { c: 'bg-blue-50 text-[#2c57c5] border-blue-200', icon: <CheckCircle2 className="h-3 w-3"/>, label: 'Aprobado' },
    rechazado: { c: 'bg-red-50 text-red-700 border-red-200', icon: <AlertTriangle className="h-3 w-3"/>, label: 'Rechazado' },
    vencido:   { c: 'bg-red-100 text-red-800 border-red-300', icon: <AlertTriangle className="h-3 w-3"/>, label: 'Vencido' },
  }[status] || { c: 'bg-slate-50 text-slate-600 border-slate-200', icon: null, label: status };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border ${map.c}`}>
      {map.icon} {map.label}
    </span>
  );
}

function PaymentRow({ provider, store, amount, status, time, alert, dark }) {
  const border = dark ? 'border-slate-800' : 'border-slate-200';
  const hover = dark ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50';
  const primary = dark ? 'text-slate-100' : 'text-slate-900';
  const sec = dark ? 'text-slate-400' : 'text-slate-500';
  return (
    <div className={`border ${border} rounded-md p-3 flex items-center gap-4 ${hover} hover:-translate-y-[1px] hover:shadow-sm transition-all cursor-pointer ${alert ? 'border-l-[3px] !border-l-red-500' : ''}`}>
      <div className={`h-10 w-10 rounded-md ${dark ? 'bg-slate-800' : 'bg-slate-100'} flex items-center justify-center ${sec}`}>
        <Building2 className="h-5 w-5"/>
      </div>
      <div className="flex-1">
        <p className={`text-sm font-semibold ${primary}`}>{provider}</p>
        <p className={`text-xs ${sec}`}>{store} · {time}</p>
      </div>
      <div className="text-right">
        <p className={`text-sm font-bold ${primary}`} style={{fontVariantNumeric:'tabular-nums'}}>{amount}</p>
        <div className="mt-1"><StatusBadge status={status}/></div>
      </div>
    </div>
  );
}

function ActivityItem({ initials, name, action, ref, amount, time, tone, dark }) {
  const toneMap = {
    green: 'bg-emerald-500', blue: 'bg-[#2c57c5]', red: 'bg-red-500', slate: 'bg-slate-500',
  };
  const primary = dark ? 'text-slate-100' : 'text-slate-900';
  const sec = dark ? 'text-slate-400' : 'text-slate-500';
  return (
    <div className="flex items-start gap-3">
      <div className={`h-7 w-7 rounded-full ${toneMap[tone]} flex items-center justify-center text-white text-[10px] font-semibold shrink-0`}>{initials}</div>
      <div className="flex-1 min-w-0">
        <p className={`text-xs ${primary}`}><span className="font-semibold">{name}</span> {action} <span className="font-mono text-[#2c57c5]">{ref}</span></p>
        <p className={`text-[10px] ${sec}`}>{amount} · {time}</p>
      </div>
    </div>
  );
}

function DetailView({ dark }) {
  const bgSurface = dark ? 'bg-slate-900' : 'bg-white';
  const border = dark ? 'border-slate-800' : 'border-slate-200';
  const textPrimary = dark ? 'text-slate-100' : 'text-slate-900';
  const textSec = dark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button className={`h-8 w-8 rounded-md ${dark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'} flex items-center justify-center`}><ArrowLeft className="h-4 w-4"/></button>
          <div>
            <h1 className={`text-2xl font-bold ${textPrimary}`} style={{fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em'}}>Pago #A5F3-517925131</h1>
            <p className={`text-xs ${textSec}`}>Café del Carmen · Antigua · Creado hoy 09:15</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button className={`h-9 px-4 border ${border} rounded-md text-sm ${textSec} hover:${dark?'bg-slate-800':'bg-slate-50'} flex items-center gap-1.5`}><Download className="h-4 w-4"/> Exportar</button>
          <button className="h-9 px-5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md flex items-center gap-1.5">✓ Aprobar</button>
          <button className="h-9 px-5 border border-red-300 text-red-700 hover:bg-red-50 text-sm font-medium rounded-md flex items-center gap-1.5">✗ Rechazar</button>
        </div>
      </div>

      {/* Rejection prev banner */}
      <div className={`mb-6 rounded-lg border border-amber-200 ${dark ? 'bg-amber-950/30' : 'bg-amber-50'} p-4 flex items-start gap-3 animate-fadeup`}>
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5"/>
        <div>
          <p className="text-sm font-semibold text-amber-900">Factura re-enviada tras rechazo previo (intento #2)</p>
          <p className={`text-xs text-amber-800 mt-1`}><strong>Motivo del rechazo anterior:</strong> Falta comprobante bancario legible</p>
          <p className="text-[11px] text-amber-700 mt-1 italic">Revisar si el problema fue corregido antes de aprobar.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* LEFT: info + timeline */}
        <div className="col-span-1 space-y-4">
          {/* Info card */}
          <div className={`${bgSurface} border ${border} rounded-lg p-6 animate-fadeup`}>
            <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${textSec} mb-2`}>Monto</p>
            <p className={`text-4xl font-bold ${textPrimary}`} style={{fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em', fontVariantNumeric:'tabular-nums'}}>Q9,986.00</p>
            <div className={`mt-4 pt-4 border-t ${border} space-y-3`}>
              <InfoRow dark={dark} icon={Building2} label="Proveedor" value="Café del Carmen"/>
              <InfoRow dark={dark} icon={User} label="Tienda" value="Antigua Guatemala"/>
              <InfoRow dark={dark} icon={Calendar} label="Fecha" value="24 feb 2026"/>
              <InfoRow dark={dark} icon={FileText} label="Serie / DT" value="AE3F6115 / 517925131"/>
            </div>
          </div>

          {/* Timeline */}
          <div className={`${bgSurface} border ${border} rounded-lg p-6 animate-fadeup`} style={{animationDelay: '100ms'}}>
            <h3 className={`font-semibold ${textPrimary} mb-4`} style={{fontFamily: 'Manrope, sans-serif'}}>Estado</h3>
            <div className="space-y-4">
              <TimelineStep dark={dark} done label="Creado" who="Carlos R." when="24 feb · 09:15"/>
              <TimelineStep dark={dark} done label="Pendiente de aprobación" who="Enviado a gerencia" when="24 feb · 09:16"/>
              <TimelineStep dark={dark} done label="Aprobado" who="Andrea N." when="24 feb · 10:22"/>
              <TimelineStep dark={dark} active label="En proceso de pago" who="Sistema" when="En curso" pulse/>
              <TimelineStep dark={dark} label="Pagado" who="Pendiente" when=""/>
              <TimelineStep dark={dark} label="Ingresado a ERP" who="Pendiente" when="" last/>
            </div>
          </div>
        </div>

        {/* RIGHT: tabs */}
        <div className={`col-span-2 ${bgSurface} border ${border} rounded-lg overflow-hidden animate-fadeup`} style={{animationDelay: '150ms'}}>
          <div className={`flex border-b ${border}`}>
            <Tab active label="Comprobantes" count="2" icon={FileText}/>
            <Tab label="Chat" count="4" icon={MessageSquare} dark={dark}/>
            <Tab label="Actividad" count="8" icon={Activity} dark={dark}/>
          </div>
          <div className={`p-6 ${dark ? 'bg-slate-950/30' : 'bg-slate-50/50'}`}>
            <div className={`${bgSurface} rounded-lg border ${border} overflow-hidden`}>
              {/* Fake receipt preview */}
              <div className="aspect-[4/5] max-h-[520px] flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 relative">
                <div className={`${bgSurface} border-2 ${dark ? 'border-slate-700' : 'border-slate-300'} shadow-lg rounded-md p-8 w-3/5 max-w-sm`}>
                  <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1">Boleta</p>
                  <p className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope, sans-serif'}}>Banrural</p>
                  <div className="text-xs text-slate-700 space-y-2 mb-6">
                    <p><strong>De:</strong> Tienda SIEMPRE Antigua</p>
                    <p><strong>A:</strong> Café del Carmen</p>
                    <p><strong>Cuenta:</strong> 3402049357</p>
                  </div>
                  <p className="text-3xl font-bold text-emerald-700 mb-2" style={{fontFamily: 'Manrope, sans-serif', fontVariantNumeric:'tabular-nums'}}>Q9,986.00</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest">24 FEB 2026 · 09:14 GT</p>
                </div>
              </div>
              <div className={`px-4 py-3 border-t ${border} flex items-center justify-between`}>
                <p className={`text-xs ${textSec}`}>boleta_bancaria_517925131.jpg · 245 KB</p>
                <div className="flex gap-2">
                  <button className={`h-8 px-3 text-xs border ${border} rounded ${textSec} hover:${dark?'bg-slate-800':'bg-slate-50'} flex items-center gap-1`}><Eye className="h-3 w-3"/> Ver completo</button>
                  <button className={`h-8 px-3 text-xs border ${border} rounded ${textSec} hover:${dark?'bg-slate-800':'bg-slate-50'} flex items-center gap-1`}><Download className="h-3 w-3"/> Descargar</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, dark }) {
  const sec = dark ? 'text-slate-500' : 'text-slate-500';
  const primary = dark ? 'text-slate-200' : 'text-slate-900';
  return (
    <div className="flex items-center gap-3">
      <Icon className={`h-4 w-4 ${sec}`}/>
      <div className="flex-1">
        <p className={`text-[10px] uppercase tracking-widest font-semibold ${sec}`}>{label}</p>
        <p className={`text-sm font-medium ${primary}`}>{value}</p>
      </div>
    </div>
  );
}

function TimelineStep({ done, active, label, who, when, pulse, last, dark }) {
  const primary = dark ? 'text-slate-100' : 'text-slate-900';
  const sec = dark ? 'text-slate-500' : 'text-slate-500';
  const bulletDone = 'bg-[#2c57c5] border-[#2c57c5]';
  const bulletActive = 'bg-white border-[#2c57c5]';
  const bulletTodo = dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-300';
  const bullet = done ? bulletDone : (active ? bulletActive : bulletTodo);
  return (
    <div className="flex gap-3 relative">
      {!last && <span className={`absolute left-[9px] top-6 w-0.5 h-full ${done ? 'bg-[#2c57c5]' : (dark?'bg-slate-800':'bg-slate-200')}`} aria-hidden="true"/>}
      <div className="relative">
        <span className={`block h-5 w-5 rounded-full border-2 ${bullet} ${pulse ? 'animate-pulse' : ''}`}>
          {done && <span className="block h-full w-full flex items-center justify-center text-white text-[10px] font-bold">✓</span>}
          {active && <span className="absolute inset-1 rounded-full bg-[#2c57c5]"/>}
        </span>
        {pulse && <span className="absolute inset-0 rounded-full bg-[#2c57c5]/30 animate-ping"/>}
      </div>
      <div className="pb-2">
        <p className={`text-sm font-medium ${done||active ? primary : sec}`}>{label}</p>
        <p className={`text-[11px] ${sec}`}>{who}{when && ` · ${when}`}</p>
      </div>
    </div>
  );
}

function Tab({ active, label, count, icon: Icon, dark }) {
  const activeC = 'text-[#2c57c5] border-b-2 border-[#2c57c5]';
  const inactiveC = dark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700';
  return (
    <button className={`px-6 h-12 flex items-center gap-2 text-sm font-semibold ${active ? activeC : inactiveC} transition-colors`}>
      <Icon className="h-4 w-4"/> {label}
      {count && <span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? 'bg-[#2c57c5]/10 text-[#2c57c5]' : 'bg-slate-200 text-slate-600'}`}>{count}</span>}
    </button>
  );
}
